/* eslint-disable no-underscore-dangle */
import { debounce } from 'lodash';

/** Object to store debounced handlers for each table */
const debouncedHandlers = {};

/**
 * Get a debounced handler for a specific table or item based on its identifier.
 *
 * @param {string} identifier - The identifier
   (e.g., assignedId, table name, etc.) for the specific handler.
 * @param {function} callback - The callback function to debounce.
 * @returns {function} - The debounced function for the given identifier.
 */

/** commenting original implementation out but leaving it here incase we run into
any issue from this update...
we needed to add flexibility by passing in a dynamic delay so that we
could essentially flush any changes to an inherited item/swap scenario by setting delay to 0.
This approach is the most miniamlly invasive. Any other approach would cause us to have to
reorganize the debounce logic in all our tables
*/

// export const getDebouncedHandlerForTable = (identifier, callback) => {
//   /** If no debounced handler exists for this identifier, create it */
//   if (!debouncedHandlers[identifier]) {
//     debouncedHandlers[identifier] = debounce((...args) => {
//       debouncedHandlers[identifier]._latestCallback?.(...args);
//     }, 4000); /** 4-second debounce */
//   }

//   /** Always update to the latest callback on component re-renders */
//   debouncedHandlers[identifier]._latestCallback = callback;

//   /** Return the debounced handler for this specific identifier */
//   return debouncedHandlers[identifier];
// };

export const getDebouncedHandlerForTable = (identifier, callback, delay = 4000) => {
  const existing = debouncedHandlers[identifier];

  /** recreate if delay changed */
  if (!existing || existing._delay !== delay) {
    if (existing?.cancel) {
      existing.cancel(); /** clean up old one */
    }

    const debounced = debounce((...args) => {
      debouncedHandlers[identifier]._hasPending = false;
      debouncedHandlers[identifier]._latestCallback?.(...args);
    }, delay);

    /** Wrapped so callers can ask whether an edit is still waiting to save -
     * lodash 4.17's debounce exposes no pending() of its own. */
    const handler = (...args) => {
      handler._hasPending = true;
      return debounced(...args);
    };
    handler.flush = () => debounced.flush();
    handler.cancel = () => {
      handler._hasPending = false;
      debounced.cancel();
    };
    handler._hasPending = false;
    handler._delay = delay;

    debouncedHandlers[identifier] = handler;
  }

  debouncedHandlers[identifier]._latestCallback = callback;

  return debouncedHandlers[identifier];
};

/**
 * Flush the debounced function for a specific identifier.
 */
export const flushDebounceForTable = (identifier) => {
  const handler = debouncedHandlers[identifier];
  if (handler?.flush) {
    handler.flush();
  }
};

/**
 * Whether an edit for this identifier is still waiting in the debounce (not
 * yet sent). Tables use it to avoid re-syncing local state from `content`
 * over edits the coach has made but that haven't been saved yet.
 */
export const hasPendingDebounceForTable = (identifier) => Boolean(
  debouncedHandlers[identifier]?._hasPending,
);

/**
 * Cancel the debounced function for a specific identifier, discarding any pending call.
 */
export const cancelDebounceForTable = (identifier) => {
  const handler = debouncedHandlers[identifier];
  if (handler?.cancel) {
    handler.cancel();
  }
};
